import { type FC, type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import styles from './InlineEditName.module.css';

interface InlineEditNameProps {
  schemaId: string;
  name: string;
  onRename: (schemaId: string, newName: string) => Promise<void>;
}

export const InlineEditName: FC<InlineEditNameProps> = ({ schemaId, name, onRename }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEditing = useCallback(() => {
    cancelledRef.current = false;
    setValue(name);
    setError('');
    setEditing(true);
  }, [name]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    setEditing(false);
    setValue(name);
    setError('');
  }, [name]);

  const save = useCallback(async () => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }

    const trimmed = value.trim();
    if (trimmed === name) {
      setEditing(false);
      return;
    }

    if (!trimmed) {
      setError('Name cannot be empty');
      return;
    }

    setSaving(true);
    try {
      await onRename(schemaId, trimmed);
      setEditing(false);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to rename');
    } finally {
      setSaving(false);
    }
  }, [value, name, onRename, schemaId]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        save();
      } else if (e.key === 'Escape') {
        cancel();
      }
    },
    [save, cancel],
  );

  if (editing) {
    return (
      <div className={styles.editContainer}>
        <input
          ref={inputRef}
          className={styles.editInput}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError('');
          }}
          onKeyDown={handleKeyDown}
          onBlur={save}
          disabled={saving}
        />
        {error && <span className={styles.editError}>{error}</span>}
      </div>
    );
  }

  return (
    <div className={styles.nameRow}>
      <span className={styles.nameText}>{name}</span>
      <button
        className={styles.renameButton}
        onClick={(e) => {
          e.stopPropagation();
          startEditing();
        }}
        title="Rename"
        aria-label="Rename schema"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
          <path d="m15 5 4 4" />
        </svg>
      </button>
    </div>
  );
};
